import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import TrialBanner from './TrialBanner';
import { useState, useEffect } from 'react';
import { getClients, getUnreadSalesCount } from '../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import AlertsBell from './AlertsBell';
import {
  LayoutDashboard, Building2, List, Landmark, RefreshCw, BookOpen, FileText,
  ArrowLeftRight, ClipboardCheck, Filter, Lightbulb, Network, TrendingUp,
  DollarSign, Globe, ShieldCheck, Mail, Settings, LogOut, User,
  Gauge, AlertTriangle, CreditCard, Database
} from 'lucide-react';

const navSections = [
  {
    title: 'Overview',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/close-readiness', label: 'Close Readiness', icon: Gauge },
      { path: '/clients', label: 'Client Profiles', icon: Building2 },
      { path: '/analytics', label: 'Analytics', icon: TrendingUp },
    ]
  },
  {
    title: 'Transactions',
    items: [
      { path: '/transactions', label: 'Transactions', icon: List },
      { path: '/exception-queue', label: 'Exception Queue', icon: AlertTriangle },
      { path: '/bank-feeds', label: 'Bank Feeds', icon: Landmark },
      { path: '/recurring', label: 'Recurring', icon: RefreshCw },
      { path: '/journal-entries', label: 'Journal Entries', icon: BookOpen },
    ]
  },
  {
    title: 'Reconciliation',
    items: [
      { path: '/recon-center', label: 'Recon Center', icon: ArrowLeftRight },
      { path: '/credit-card', label: 'Credit Card Controls', icon: CreditCard },
      { path: '/reconciliation', label: 'Statement Match', icon: CreditCard },
    ]
  },
  {
    title: 'Review & Rules',
    items: [
      { path: '/missing-documents', label: 'Missing Documents', icon: FileText },
      { path: '/review', label: 'Review Queue', icon: ClipboardCheck },
      { path: '/rules', label: 'Rules Engine', icon: Filter },
      { path: '/corrections', label: 'Learning Hub', icon: Lightbulb },
      { path: '/knowledge-graph', label: 'Knowledge Graph', icon: Network },
    ]
  },
  {
    title: 'Reports',
    items: [
      { path: '/financial-statements', label: 'Financials', icon: FileText },
      { path: '/budget', label: 'Budget vs Actual', icon: DollarSign },
      { path: '/currency', label: 'Multi-Currency', icon: Globe },
      { path: '/audit', label: 'Audit Trail', icon: ShieldCheck },
    ]
  },
  {
    title: 'Admin',
    items: [
      { path: '/import/botkeeper', label: 'Import from Botkeeper', icon: Database },
      { path: '/sales-inbox', label: 'Sales Inbox', icon: Mail },
      { path: '/settings', label: 'Settings', icon: Settings },
    ]
  }
];

export default function Layout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [activeClient, setActiveClient] = useState('');
  const [unreadSales, setUnreadSales] = useState(0);

  useEffect(() => {
    getClients().then(res => {
      setClients(res.data.clients || []);
      if (res.data.clients?.length > 0 && !activeClient) setActiveClient(res.data.clients[0].client_id);
    }).catch(() => {});
    getUnreadSalesCount().then(res => setUnreadSales(res.data.count || 0)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen flex" style={{ background: '#F7F6F2' }}>
      {/* Sidebar */}
      <aside className="w-[260px] flex-shrink-0 flex flex-col dark-scrollbar overflow-y-auto" style={{ background: '#111815' }}>
        <div className="h-16 flex items-center px-5" style={{ borderBottom: '1px solid #2D3330' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0A3622' }}>
              <LayoutDashboard size={15} className="text-white" />
            </div>
            <span className="font-heading text-[15px] font-semibold text-white tracking-tight">SnuggleMind</span>
          </div>
        </div>

        <nav className="flex-1 py-3 px-3" data-testid="main-navigation">
          {navSections.map((section) => (
            <div key={section.title} className="mb-3">
              <p className="text-[10px] font-body font-semibold uppercase tracking-[0.18em] px-3 mb-1.5" style={{ color: '#6B7280' }}>
                {section.title}
              </p>
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-[7px] text-[13px] font-body rounded-lg transition-all duration-150 ${
                        isActive ? 'font-medium' : 'hover:bg-white/[0.04]'
                      }`
                    }
                    style={({ isActive }) => ({
                      color: isActive ? '#FFFFFF' : '#9CA3AF',
                      background: isActive ? 'rgba(217,138,74,0.12)' : undefined,
                      borderLeft: isActive ? '3px solid #D98A4A' : '3px solid transparent',
                    })}
                  >
                    <item.icon size={16} strokeWidth={1.8} />
                    <span>{item.label}</span>
                    {item.path === '/sales-inbox' && unreadSales > 0 && (
                      <span className="ml-auto px-1.5 py-0.5 text-[10px] rounded-full font-mono leading-none" style={{ background: '#D98A4A', color: '#fff' }}>{unreadSales}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-4" style={{ borderTop: '1px solid #2D3330' }}>
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#2D3330' }}>
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <User size={14} style={{ color: '#9CA3AF' }} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-white truncate">{user?.name || 'User'}</p>
              <p className="text-[11px] truncate" style={{ color: '#6B7280' }}>{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="logout-button" className="w-full flex items-center gap-2 px-3 py-2 text-[13px] rounded-lg transition-colors duration-150 hover:bg-white/[0.04]" style={{ color: '#9CA3AF' }}>
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-auto">
        <header className="h-16 flex items-center justify-between px-6 flex-shrink-0" style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E3DB' }}>
          <div className="flex items-center gap-8">
            <h1 className="font-heading text-[20px] font-semibold tracking-[-0.01em]" data-testid="page-title" style={{ color: '#111815' }}>{title}</h1>
            <nav className="hidden lg:flex items-center gap-6 pl-6" style={{ borderLeft: '1px solid #E5E3DB' }} data-testid="app-top-nav">
              <a href="/" className="text-[13px] font-body font-medium transition-colors" style={{ color: '#4B5563' }} data-testid="top-nav-home">Home</a>
              <a href="/solutions/bookkeeping" className="text-[13px] font-body font-medium transition-colors hover:text-[#0A3622]" style={{ color: '#4B5563' }}>Solutions</a>
              <a href="/connect" className="text-[13px] font-body font-medium transition-colors hover:text-[#0A3622]" style={{ color: '#4B5563' }}>Connect</a>
              <a href="/resources" className="text-[13px] font-body font-medium transition-colors hover:text-[#0A3622]" style={{ color: '#4B5563' }}>Resources</a>
              <a href="/tour" className="text-[13px] font-body font-medium transition-colors hover:text-[#0A3622]" style={{ color: '#4B5563' }} data-testid="top-nav-tour">Tour</a>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {clients.length > 0 && (
              <div className="flex items-center gap-2.5">
                <span className="text-[10px] font-body uppercase tracking-[0.18em] font-semibold" style={{ color: '#9CA3AF' }}>Client</span>
                <Select value={activeClient} onValueChange={setActiveClient}>
                  <SelectTrigger className="w-48 h-9 rounded-full text-[13px] font-body" style={{ borderColor: '#E5E3DB' }} data-testid="client-selector">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <AlertsBell />
            <a
              href="/pricing"
              className="hidden md:inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12.5px] font-body font-semibold transition-all hover:scale-[1.02]"
              style={{ background: '#D98A4A', color: '#FFFFFF' }}
              data-testid="top-upgrade-cta"
            >
              Upgrade
            </a>
          </div>
        </header>

        <TrialBanner />

        <div className="flex-1 p-6 animate-fade-in">
          {children}
        </div>

        <footer className="px-6 py-3 flex items-center justify-between font-body text-[11px]" style={{ borderTop: '1px solid #E5E3DB', color: '#9CA3AF' }}>
          <span>&copy; {new Date().getFullYear()} Snugglemind Inc.</span>
          <div className="flex items-center gap-3">
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="footer-terms">Terms</a>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="footer-privacy">Privacy</a>
            <a href="/subscription-agreement" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="footer-subscription">Subscription</a>
          </div>
        </footer>
      </main>
    </div>
  );
}
